import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import HeroModule from '$src/js/modules/hero/HeroModule';
import { HeroState } from '$src/js/states/HeroState.svelte';

export class ModelService {
  private envMap: THREE.Texture | null = null;
  private jerseyModel: THREE.Object3D | null = null;
  private glassMat: THREE.MeshPhysicalMaterial | null = null;
  private glassReflectiveMat: THREE.MeshPhysicalMaterial | null = null;

  /**
   * Loads the environment map and jersey model.
   * @returns A promise that resolves when the assets are loaded.
   */
  readonly loadAssets = async (): Promise<void> => {
    const loader = HeroModule.getInstance().loader;
    const scene = HeroModule.getInstance().scene.getScene();

    // Register loading tasks
    loader.registerTask('envMap', 2);
    loader.registerTask('gltfModel', 4);

    if (!scene) {
      return;
    }

    try {
      const texture = await this.loadEnvMap().catch((err: unknown) => {
        console.warn('Env map failed to load, continuing without:', err);
        return null;
      });

      loader.completeTask('envMap');

      if (texture) {
        this.envMap = texture;
        scene.environment = texture;
        scene.background = null;
        scene.environmentRotation.y = -2.19159265358979;
        scene.backgroundRotation.y = -2.19159265358979;
      }

      await this.loadModel();
      loader.completeTask('gltfModel');
      HeroState.ready = true;
    } catch (err) {
      console.error('Failed to load assets:', err);
    }
  };

  /**
   * Loads the environment map.
   * @returns A promise that resolves with the loaded texture.
   */
  private readonly loadEnvMap = (): Promise<THREE.Texture> => {
    return new Promise((resolve, reject) => {
      const loader = new EXRLoader();

      loader.load(
        'https://yellow-b-global.b-cdn.net/production/the-architect/twilight_sunset_1k.exr?v=5',
        (texture) => {
          texture.mapping = THREE.EquirectangularReflectionMapping;
          resolve(texture);
        },
        undefined,
        reject,
      );
    });
  };

  /**
   * Creates a glass material for the glass case model.
   * @returns The created glass material.
   */
  private readonly createGlassMaterial = (): THREE.MeshPhysicalMaterial => {
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0xc0cbcd,
      transmission: 1.0,
      ior: 1.5,
      thickness: 0.008,
      roughness: 0.015,
      metalness: 0.0,
      envMapIntensity: 1.4,
      attenuationColor: new THREE.Color(0xd6eef5),
      attenuationDistance: 0.2,
      transparent: true,
      opacity: 0.1,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    this.glassMat = glassMat;
    return glassMat;
  };

  /**
   * Creates a darker and more reflective glass material for the corners of the glass case model.
   * @returns The created reflective glass material.
   */
  private readonly createReflectiveGlassMaterial = (): THREE.MeshPhysicalMaterial => {
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0x8a9ba3,
      transmission: 0.6,
      ior: 1.5,
      thickness: 0.008,
      roughness: 0.015,
      metalness: 0.05,
      envMapIntensity: 2.0,
      attenuationColor: new THREE.Color(0x606060),
      attenuationDistance: 0.1,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    this.glassReflectiveMat = mat;
    return mat;
  };

  /**
   * Loads the jersey model and applies materials to it and positions it.
   * @returns A promise that resolves when the model is loaded.
   */
  private readonly loadModel = (): Promise<void> => {
    const displayGroup = HeroModule.getInstance().scene.getDisplayGroup();

    if (!displayGroup) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      const glassMat = this.createGlassMaterial();
      const reflectiveMat = this.createReflectiveGlassMaterial();

      loader.load(
        'https://yellow-b-global.b-cdn.net/production/the-architect/model/shirt-in-pedestal.gltf',
        (gltf) => {
          const model = gltf.scene;

          const maxAnisotropy = HeroModule.getInstance().scene.getMaxAnisotropy();

          model.traverse((child) => {
            if (!(child instanceof THREE.Mesh)) {
              return;
            }

            const mat = child.material as THREE.MeshStandardMaterial;
            mat.envMapIntensity = mat.envMapIntensity || 0.5;

            (Object.values(mat) as unknown[]).forEach((value) => {
              if (value instanceof THREE.Texture) {
                value.anisotropy = maxAnisotropy;
                value.needsUpdate = true;
              }
            });
          });

          const glassNode = model.getObjectByName('glass_case');
          if (glassNode) {
            glassNode.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.material = glassMat;
              }
            });
          }

          const reflectiveNode = model.getObjectByName('glass_reflective_faces');
          if (reflectiveNode) {
            reflectiveNode.traverse((child) => {
              if (child instanceof THREE.Mesh) {
                child.material = reflectiveMat;
              }
            });
          }

          this.jerseyModel = model;
          HeroModule.getInstance().texture.collectSkinMeshes(model);
          displayGroup.add(model);

          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          model.position.x -= center.x;
          model.position.z -= center.z;

          // Store center for responsive positioning
          model.userData.centerY = center.y;

          // Apply responsive scale and position
          this.updateModelTransform();

          // HeroModule.getInstance().gui.setup();
          HeroModule.getInstance().scene.markDirty();
          resolve();
        },
        undefined,
        reject,
      );
    });
  };

  /**
   * Returns the environment map.
   * @returns The environment map.
   */
  readonly getEnvMap = (): THREE.Texture | null => this.envMap;

  /**
   * Returns the jersey model.
   * @returns The jersey model.
   */
  readonly getJerseyModel = (): THREE.Object3D | null => this.jerseyModel;

  /**
   * Returns the glass material.
   * @returns The glass material.
   */
  readonly getGlassMat = (): THREE.MeshPhysicalMaterial | null => this.glassMat;

  /**
   * Returns the glass reflective material.
   * @returns The glass reflective material.
   */
  readonly getGlassReflectiveMat = (): THREE.MeshPhysicalMaterial | null => this.glassReflectiveMat;

  /**
   * Updates the model's scale and position based on viewport orientation.
   */
  readonly updateModelTransform = (): void => {
    if (!this.jerseyModel) {
      return;
    }

    const isPortrait = window.innerHeight > window.innerWidth;
    const centerY = (this.jerseyModel.userData.centerY as number | undefined) ?? 0;

    if (isPortrait) {
      this.jerseyModel.scale.set(1, 1, 1);
      this.jerseyModel.position.y = -centerY - 0.3;
    } else {
      this.jerseyModel.scale.set(1.2, 1.2, 1.2);
      this.jerseyModel.position.y = -centerY - 0.5;
    }

    HeroModule.getInstance().scene.markDirty();
  };

  /**
   * Disposes the environment map.
   */
  readonly dispose = (): void => {
    if (this.envMap) {
      this.envMap.dispose();
      this.envMap = null;
    }
  };
}
